import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { reportError } from '../../lib/reportError';
import { PaymentSource, CVV_ERROR } from '../../constants';
import { hrefFor } from '../../routers';
import { useGiftCode } from '../../components/GiftCode/useGiftCode';
import { useCart } from '../useCart';
import { useUser } from '../useUser';
import { useErrorModal } from '../useErrorModal';
import { useOrderConvertedTracking } from './useOrderConvertedTracking';
import { usePlaceOrderMutation } from './usePlaceOrderMutation';
import { useAffirmCheckout } from './useAffirmCheckout';
import { usePaymentError } from './usePaymentError';

export const useCheckout = () => {
  const history = useHistory();
  const { showErrorModal } = useErrorModal();
  const { user } = useUser();
  const { cart } = useCart();
  const { applyGiftCode } = useGiftCode();
  const [placeOrder] = usePlaceOrderMutation();
  const { trackOrderConverted } = useOrderConvertedTracking();
  const { placeAffirmOrder } = useAffirmCheckout();
  const { setPaymentError } = usePaymentError();

  const isCartValid =
    Boolean(cart.id) && cart.attributes.cartItems.every(item => !item.data.attributes.error);
  const isShippingAddressSelected = Boolean(cart.attributes.addressId);
  const isShippingMethodSelected = Boolean(cart.attributes.selectedShippingMethodId);
  const isPaymentMethodSelected =
    Boolean(cart.attributes.paymentMethodId) ||
    cart.attributes.paymentSource === PaymentSource.Affirm;
  const hasInvalidCoupon = cart.attributes.error?.type.name === 'Invalid Coupon';

  const canPlaceOrder =
    isCartValid && isShippingAddressSelected && isShippingMethodSelected && isPaymentMethodSelected;

  const handlePlaceOrder = useCallback(async () => {
    if (!canPlaceOrder) return;

    if (hasInvalidCoupon) {
      await applyGiftCode(null);
    }

    try {
      const { data } =
        cart.attributes.paymentSource === PaymentSource.Affirm
          ? await placeAffirmOrder()
          : await placeOrder();

      trackOrderConverted(data);

      data &&
        history.push(
          hrefFor.OrderConfirmationPage({
            orderNumber: data.checkout.data[0].attributes.orderNumber
          })
        );
    } catch (error) {
      if (error.reason !== 'canceled') {
        const errorMessage = error.networkError?.result?.errors?.[0]?.detail;
        setPaymentError(errorMessage ?? null);

        if (errorMessage !== CVV_ERROR) {
          showErrorModal(errorMessage);
          window.newrelic?.addPageAction('checkoutFailed', {
            error: error.toString(),
            message: errorMessage,
            email: user.email,
            guest: !user.isLoggedIn,
            paymentSource: cart.attributes.paymentSource,
            total: cart.attributes.paymentSummary.total.price,
            payToday: cart.attributes.paymentSummary.payToday.price,
            payLater: cart.attributes.paymentSummary.payLater.price
          });
        }

        reportError(error);
      }
    }
  }, [
    history,
    canPlaceOrder,
    cart,
    user,
    hasInvalidCoupon,
    placeOrder,
    applyGiftCode,
    showErrorModal,
    placeAffirmOrder,
    setPaymentError,
    trackOrderConverted
  ]);

  return {
    isCartValid,
    isShippingAddressSelected,
    isShippingMethodSelected,
    isPaymentMethodSelected,
    canPlaceOrder,
    handlePlaceOrder
  };
};
